import { Checkbox } from "@progress/kendo-react-inputs";
import React, { useEffect, useState } from "react";
import { getAliceRecommendedReferenceAttributeForRelation, getAliceRecommendedRelations } from "../../../../API/AliceGPT/AliceGPT";
import * as AliceGPTEvents from "../../../../Context/AliceGPT/AliceGPTContextEvents";
import useAliceGPT from "../../../../Hooks/AliceGPT/useAliceGPT";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import Loader from "../../../Shared/Loader/loader";
import SelectedCardinality from "../../../LinkedEntities/Field/SelectedCardinality";
import { USER_TO_USER_RELATION } from "./variables";
import { getDatalakeImageUrl } from "../../../Helpers/DateLakeStorage";
import useFeatureFlags from "../../../../Hooks/FeatureFlag/useFeatureFlags";
import { ATTRIBUTE_LABEL } from "../../../Helpers/Labels/AttributeLabel";
import { Tooltip } from '@progress/kendo-react-tooltip';
import RecommendedRelationCard from "../../../Shared/Recommendation/Relations/RecommendedRelationCard";
import FailedToLoadError from "../../../Shared/Recommendation/Error/FailedToLoadError";
import { useTranslation } from "react-i18next";
import { getEntitiesExceedingAttributeLimitOnCreatingRelations } from "./utils";

function AliceRecommendedRelations({ isFromExtension }) {

    const { t } = useTranslation("create_project/alice_recommended_relations", { useSuspense: false });

    const [state, dispatch] = useAliceGPT();
    const featureFlags = useFeatureFlags();

    const [loading, setLoading] = useState(false);
    const [referenceAttributesLoaded, setReferenceAttributesLoaded] = useState(true);

    let numRelationsSelected = state.relations.filter(relation => relation.selected).length;

    useEffect(() => {
        if (selectedEntitiesChanged()) {
            debugger;
            dispatch({
                type: AliceGPTEvents.UPDATE_PREVIOUS_SELECTED_ENTITY_NAME
            })
            dispatch({
                type: AliceGPTEvents.TOGGLE_LOAD_EDIT_PAGE_CONFIGURATION
            })
            dispatch({
                type: AliceGPTEvents.RESET_RELATIONS_LOADED
            })
            const entityNames = state.entities.filter(entity => entity.checked && !entity.isUserEntity).map(entity => entity.name);
            setLoading(true);
            getAliceRecommendedRelations(entityNames, state.form.model, isFromExtension)
                .then(response => {
                    // filter relation which has from and to as same entity and
                    const filteredResponse = response.filter(relation => relation.from !== relation.to)
                    // filter one relation for each pair of entity
                    const uniqueRelations = [];
                    const uniqueRelationKeys = {};
                    for (const relation of filteredResponse) {
                        const relationKey = `${relation.from}-${relation.to}`;
                        const reverseRelationKey = `${relation.to}-${relation.from}`;
                        if (!uniqueRelationKeys[relationKey] && !uniqueRelationKeys[reverseRelationKey]) {
                            uniqueRelationKeys[relationKey] = true;
                            uniqueRelations.push(relation);
                        }
                    }
                    const relations = uniqueRelations.map(relationToStateArrayElement);
                    dispatch({
                        type: AliceGPTEvents.RELATIONS_LOADED,
                        payload: relations
                    })
                    setReferenceAttributesLoaded(false);
                })
                .catch(error => {
                    dispatch({
                        type: AliceGPTEvents.FACED_ERROR,
                        payload: {
                            error: error.message
                        }
                    })
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            dispatch({
                type: AliceGPTEvents.RELATIONS_LOADED,
                payload: state.relations
            })
        }
    }, []);

    function selectedEntitiesChanged() {
        const selectedEntities = state.namesOfSelectedEntities;
        const previousSelectedEntities = state.previousNamesOfSelectedEntities;
        if (selectedEntities.length !== previousSelectedEntities.length) {
            state.relations.forEach(relation => relation.selected = false);
            return true;
        }
        for (const selectedEntity of selectedEntities) {
            if (!previousSelectedEntities.includes(selectedEntity)) {
                return true;
            }
        }
        return false;
    }

    function relationToStateArrayElement(relation) {
        return {
            from: relation.from || relation[0],
            to: relation.to || relation[1],
            cardinality: relation.cardinality || relation[2],
            showReferenceAttribute: relation.cardinality === "1-*" && doesFromEntityHaveValidAttributes(relation),
            referenceAttribute: "",
            checked: false,
            selected: true
        }
    }

    useEffect(() => {
        if (!loading && !state.initialAttributesLoaded) {
            loadReferenceAttributesForRelations();
        }
    }, [state.initialAttributesLoaded])

    async function loadReferenceAttributesForRelations() {
        const results = [];
        state.relations.forEach((relation, index) => {
            const fromEntityHasValidAttributes = doesFromEntityHaveValidAttributes(relation);
            if (
                relation.cardinality === "1-*" &&
                fromEntityHasValidAttributes &&
                !relation.isStatic
            ) {
                const response = getRecommendedAttribute(index);
                results.push(response);
            }
        });
        for (const response of results) {
            dispatch({
                type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
                payload: { relationIndex: response.relationIndex, referenceAttribute: response.attributeName }
            })
        }
        dispatch({
            type: AliceGPTEvents.INITIAL_REFERENCE_ATTRIBUTES_LOADED
        })
    }

    function doesFromEntityHaveValidAttributes(relation) {
        const fromEntity = state.entities.find(entity => entity.name === relation.from);
        const fromEntityAttributes = fromEntity.attributes.filter(attribute => attribute.selected && attribute.type === "string");
        return fromEntityAttributes.length > 0;
    }
    
    function toggleCheckbox(relationIndex) {
        if (state.relations[relationIndex].selected) {
            dispatch({
                type: AliceGPTEvents.RELATION_DESELECTED,
                payload: { relationIndex }
            });
        } else {
            dispatch({
                type: AliceGPTEvents.RELATION_SELECTED,
                payload: { relationIndex }
            });
        }
    }

    function toggleShowReferenceAttribute(relationIndex) {
        if (!state.relations[relationIndex].referenceAttribute) {
            const attribute = getRecommendedAttribute(relationIndex)
            if (attribute.attributeName) {
                dispatch({
                    type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
                    payload: {
                        relationIndex, referenceAttribute: attribute.attributeName
                    }
                });
            } else {
                dispatch({
                    type: AliceGPTEvents.SHOW_REFERENCE_ATTRIBUTE_TOGGLED,
                    payload: { relationIndex }
                })
            }
        }
        dispatch({
            type: AliceGPTEvents.SHOW_REFERENCE_ATTRIBUTE_TOGGLED,
            payload: { relationIndex }
        });
    }

    function getRecommendedAttribute(relationIndex) {
        const relation = state.relations[relationIndex];
        const fromEntity = state.entities.find(entity => entity.name === relation.from);
        const selectedFromEntityAttributes = fromEntity.attributes.filter(attribute => attribute.selected && attribute.type === "String");

        if (selectedFromEntityAttributes.length === 0) {
            return { relationIndex, attributeName: null };
        }

        return { relationIndex, attributeName: selectedFromEntityAttributes[0].name };
    }

    function getSelectedAttributeNameOfEntity(entityName) {
        const entity = state.entities.find(entity => entity.name === entityName);
        const selectedTextAttributes = entity.attributes.filter(attribute => attribute.selected && attribute.type === "String");
        return selectedTextAttributes.map(attribute => attribute.name);
    }

    function handleReferenceAttributeChange(event, relationIndex) {
        dispatch({
            type: AliceGPTEvents.REFERENCE_ATTRIBUTE_CHANGED,
            payload: { referenceAttribute: event.target.value, relationIndex }
        });
    }

    function handleCardinalityChange(event, relationIndex) {
        dispatch({
            type: AliceGPTEvents.CARDINALITY_CHANGED,
            payload: { cardinality: event.target.value, relationIndex }
        });
    }

    function handleSwap(event, relationIndex) {
        event.stopPropagation();
        dispatch({
            type: AliceGPTEvents.RELATION_SWAP,
            payload: { relationIndex }
        });
    }

    function getIsAttributeLimitExceeded() {
        const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
            state.entities,
            state.relations,
            state.subscription
        )
        if (entitiesWithAttributeLimitExceeded.length > 0) {
            return (
                <span className="text-danger">
                    {t("max_attributes_exceeded", {
                        limit: state.subscription.maxAttributesPerApp,
                        entitiesWithAttributeLimitExceeded: entitiesWithAttributeLimitExceeded.join(", ")
                    })}
                </span>
            )
        }
        return <></>
    }

    if (loading) {
        return (
            <div className="h-100 w-100 d-flex flex-column justify-content-center align-items-center">
                <img src={getDatalakeImageUrl("/loaders/RecommendationLoader.gif")} alt=""/>
                <h3 className="mt-4">
                    {t("loading_message")}
                </h3>
            </div>
        );
    }

    if (state.error) {
        return (
            <FailedToLoadError />
        )
    }

    return (
        <>
            <div className="row">
                <div className="col-12 mt-2">
                    <Tooltip position='right' anchorElement='target' tooltipStyle={{ maxWidth: "250px" }}>
                        <h5 className="d-flex align-items-center" style={{ fontWeight: "700" }}>
                            <Checkbox
                                checked={state.relations.every(relation => relation.selected)}
                                value={state.relations.every(relation => relation.selected)}
                                onChange={() => dispatch({ type: AliceGPTEvents.TOGGLE_SELECT_ALL_RELATIONS })}
                                labelPlacement="left"
                            />&nbsp;
                            { t("select_all.label") }&nbsp;
                            <i
                                className='fa-solid fa-circle-info' style={{ opacity: "0.5" }}
                                title={t("select_all.tooltip")}
                            />
                            {
                                numRelationsSelected > state.subscription.maxRelationsPerApp &&
                                <span className="text-danger ms-2">
                                    {t("max_relations_exceeded", { maxRelations: state.subscription.maxRelationsPerApp })}
                                </span>
                            }
                            &nbsp;&nbsp;
                            {getIsAttributeLimitExceeded()}
                        </h5>
                    </Tooltip>
                </div>
            </div>
            <div className="row gy-3 gx-1">
                {
                    state.relations.length === 0 &&
                    <div className="col-12">
                        <div className="alert alert-relation">
                            {t("no_relations_found")}
                        </div>
                    </div>
                }
                {state.relations.map((relation, index) => (
                    <RecommendedRelationCard
                        key={index}
                        fromEntityName={relation.from}
                        toEntityName={relation.to}
                        cardinality={relation.cardinality}
                        addReferenceAttribute={relation.showReferenceAttribute}
                        referenceAttribute={relation.referenceAttribute}
                        referenceAttributes={getSelectedAttributeNameOfEntity(relation.from)}
                        selected={relation.selected}
                        onCardinalityChange={(event) => handleCardinalityChange(event, index)}
                        onAddReferenceAttributeChange={() => toggleShowReferenceAttribute(index)}
                        onReferenceAttributeChange={(event) => handleReferenceAttributeChange(event, index)}
                        onToggle={() => toggleCheckbox(index)}
                        onSwap={(e) => handleSwap(e, index)}
                    />
                ))}
            </div>
        </>
    );
}

export default AliceRecommendedRelations;